/**
 * The per-turn clock. [P6-timer]
 *
 * Tony asked whether the timer works at all and why hotseat was excluded from it. The honest answer was
 * that it worked nowhere: the server implemented it properly and broadcast a deadline, the online lobby
 * hardcoded `turnSeconds: 0` so no room ever enabled it, the client had no handler for the broadcast,
 * and local play had no timer of any kind. Three of those are one-line fixes; this file is the fourth.
 *
 * It is **per turn**, not per game — his words, and the right model: the clock restarts whenever the
 * seat on the clock changes, so thinking hard about one placement costs you that turn and nothing more.
 *
 * On expiry a local game does exactly what the server does to a stalled seat ([P6-timer] `autoPassSeat`):
 * draw if a tile is owed, place it at the first legal spot, and never place a meeple on the player's
 * behalf. Guessing which feature someone wanted is worse than taking nothing.
 *
 * A hybrid game is BOTH at once — some seats here, some elsewhere — and that is exactly why the local
 * clock only ever runs for a `LocalSession`. When a server is in the room, the server's deadline is the
 * only one that may auto-pass anybody; this side just displays it.
 */

export interface TurnTimerHooks {
  /** Called every ~200ms with the seconds left, or null when no clock is running. */
  readonly onTick: (secondsRemaining: number | null) => void;
  /** Called once when the clock reaches zero. Local games only — the server handles its own. */
  readonly onExpire: () => void;
}

const POLL_MS = 200;

export class TurnTimer {
  private deadline = 0;
  private handle = 0;
  /** What the clock is currently counting for; a change restarts it. */
  private key = '';
  private fired = false;

  constructor(
    private readonly seconds: number,
    private readonly hooks: TurnTimerHooks,
  ) {}

  get enabled(): boolean {
    return this.seconds > 0;
  }

  /**
   * Tell the clock what turn we are on.
   *
   * Idempotent for the same turn — `advance()` legitimately runs several times within one turn (the
   * session listener, the post-action callback, a re-render), and a clock that restarted on each of
   * those would never actually run out. The key is what identifies "the same turn": the seat plus the
   * phase it is in, so drawing and then placing does not hand the player a fresh minute.
   */
  turnBegan(key: string): void {
    if (!this.enabled) return;
    if (key === this.key) return;
    this.key = key;
    this.fired = false;
    this.deadline = performance.now() + this.seconds * 1000;
    this.start();
  }

  /** A deadline handed down by the server. Displayed, never enforced here. */
  showRemoteDeadline(msRemaining: number): void {
    this.deadline = performance.now() + msRemaining;
    this.fired = true; // display only: expiry belongs to the server
    this.key = `remote:${Math.round(this.deadline)}`;
    this.start();
  }

  stop(): void {
    window.clearInterval(this.handle);
    this.handle = 0;
    this.key = '';
    this.hooks.onTick(null);
  }

  private start(): void {
    window.clearInterval(this.handle);
    this.handle = window.setInterval(() => this.poll(), POLL_MS);
    this.poll();
  }

  private poll(): void {
    const remaining = this.deadline - performance.now();
    this.hooks.onTick(Math.max(0, Math.ceil(remaining / 1000)));
    if (remaining > 0 || this.fired) return;
    // Latch before calling out: the callback resolves the turn, which calls back in here, and an
    // unlatched timer would auto-pass the next seat too.
    this.fired = true;
    window.clearInterval(this.handle);
    this.handle = 0;
    this.hooks.onExpire();
  }
}
